'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    getAuthorizations,
    PriorAuthorization,
    AUTHORIZATION_STATUS_LABELS,
    AUTHORIZATION_STATUS_COLORS,
    AUTHORIZATION_URGENCY_LABELS,
    AUTHORIZATION_URGENCY_COLORS,
} from '@/lib/api/billing/authorization-api'
import {
    MdVerified,
    MdSearch,
    MdClose,
    MdAdd,
    MdArrowBack,
    MdArrowForward,
    MdPerson,
    MdLocalHospital,
    MdAccessTime,
    MdPriorityHigh,
} from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function AuthorizationsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('billing', 'authorizations')
    const [authorizations, setAuthorizations] = useState<PriorAuthorization[]>([])
    const [loading, setLoading] = useState(true)
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [urgencyFilter, setUrgencyFilter] = useState<string>('all')

    useEffect(() => {
        if (user?.companyId) {
            loadAuthorizations()
        }
    }, [user?.companyId])

    const loadAuthorizations = async () => {
        setLoading(true)
        try {
            const response = await getAuthorizations()
            if (response.success) {
                setAuthorizations(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar autorizações')
            }
        } catch (error) {
            toast.error('Erro ao carregar autorizações')
        } finally {
            setLoading(false)
        }
    }

    const filteredAuthorizations = authorizations.filter(auth => {
        if (searchTerm) {
            const search = searchTerm.toLowerCase()
            if (
                !auth.authorizationNumber?.toLowerCase().includes(search) &&
                !auth.client?.name?.toLowerCase().includes(search) &&
                !auth.healthPlan?.name?.toLowerCase().includes(search)
            ) {
                return false
            }
        }
        if (statusFilter !== 'all' && auth.status !== statusFilter) {
            return false
        }
        if (urgencyFilter !== 'all' && auth.urgency !== urgencyFilter) {
            return false
        }
        return true
    })

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando autorizações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MdVerified className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Autorizações Prévias
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {authorizations.length}{' '}
                            {authorizations.length === 1
                                ? 'autorização'
                                : 'autorizações'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar autorizações..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    setSearchTerm('')
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <button
                                    onClick={() =>
                                        navigate('/billing/autorizações/new')
                                    }
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Nova Autorização
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap">
                <button
                    onClick={() => setStatusFilter('all')}
                    className={`btn btn-sm ${statusFilter === 'all' ? 'btn-secondary' : 'btn-ghost'}`}
                >
                    Todas
                </button>
                <button
                    onClick={() => setStatusFilter('AUTH_DRAFT')}
                    className={`btn btn-sm ${statusFilter === 'AUTH_DRAFT' ? 'btn-ghost' : 'btn-ghost'}`}
                >
                    Rascunho
                </button>
                <button
                    onClick={() => setStatusFilter('AUTH_PENDING')}
                    className={`btn btn-sm ${statusFilter === 'AUTH_PENDING' ? 'btn-warning' : 'btn-ghost'}`}
                >
                    Pendentes
                </button>
                <button
                    onClick={() => setStatusFilter('AUTH_SUBMITTED')}
                    className={`btn btn-sm ${statusFilter === 'AUTH_SUBMITTED' ? 'btn-info' : 'btn-ghost'}`}
                >
                    Enviadas
                </button>
                <button
                    onClick={() => setStatusFilter('AUTH_APPROVED')}
                    className={`btn btn-sm ${statusFilter === 'AUTH_APPROVED' ? 'btn-success' : 'btn-ghost'}`}
                >
                    Aprovadas
                </button>
                <button
                    onClick={() => setStatusFilter('AUTH_DENIED')}
                    className={`btn btn-sm ${statusFilter === 'AUTH_DENIED' ? 'btn-error' : 'btn-ghost'}`}
                >
                    Negadas
                </button>
            </div>

            {/* Urgency Filter */}
            <div className="flex gap-4 flex-wrap">
                <select
                    value={urgencyFilter}
                    onChange={e => setUrgencyFilter(e.target.value)}
                    className="select select-bordered select-sm"
                >
                    <option value="all">Todas as Urgências</option>
                    <option value="ROUTINE">Rotina</option>
                    <option value="URGENT">Urgente</option>
                    <option value="EMERGENCY">Emergência</option>
                </select>
            </div>

            {filteredAuthorizations.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdVerified className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma autorização encontrada
                    </h3>
                    <p className="text-base-content/60 mb-6">
                        Solicite sua primeira autorização prévia
                    </p>
                    {canCreate && (
                        <button
                            onClick={() =>
                                navigate('/billing/autorizações/new')
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Nova Autorização
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredAuthorizations.map(auth => (
                        <div
                            key={auth.id}
                            onClick={() =>
                                navigate(`/billing/autorizações/${auth.id}`)
                            }
                            className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span
                                            className={`badge badge-sm ${AUTHORIZATION_STATUS_COLORS[auth.status]}`}
                                        >
                                            {AUTHORIZATION_STATUS_LABELS[auth.status]}
                                        </span>
                                        <span
                                            className={`badge badge-sm ${AUTHORIZATION_URGENCY_COLORS[auth.urgency]}`}
                                        >
                                            {auth.urgency === 'EMERGENCY' && (
                                                <MdPriorityHigh className="text-xs mr-1" />
                                            )}
                                            {AUTHORIZATION_URGENCY_LABELS[auth.urgency]}
                                        </span>
                                    </div>
                                    {auth.authorizationNumber ? (
                                        <h3 className="font-semibold text-base-content mb-1">
                                            {auth.authorizationNumber}
                                        </h3>
                                    ) : (
                                        <h3 className="font-semibold text-base-content/60 mb-1">
                                            Aguardando número
                                        </h3>
                                    )}
                                </div>
                                <MdArrowForward className="text-base-content/40 flex-shrink-0 ml-2" />
                            </div>

                            <div className="space-y-2 mb-3">
                                {auth.client && (
                                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                                        <MdPerson className="text-base" />
                                        <span className="truncate">
                                            {auth.client.name}
                                        </span>
                                    </div>
                                )}
                                {auth.healthPlan && (
                                    <div className="flex items-center gap-2 text-sm text-base-content/60">
                                        <MdLocalHospital className="text-base" />
                                        <span className="truncate">
                                            {auth.healthPlan.name}
                                        </span>
                                    </div>
                                )}
                                {auth.diagnosis && (
                                    <p className="text-xs text-base-content/50 truncate">
                                        {auth.diagnosis}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                <div className="flex items-center gap-2 text-xs text-base-content/60">
                                    <MdAccessTime className="text-sm" />
                                    <span>
                                        {format(
                                            new Date(auth.requestDate),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )}
                                    </span>
                                </div>
                                <span className="text-sm font-semibold text-secondary">
                                    {formatCurrency(auth.totalRequested)}
                                </span>
                            </div>

                            {auth.validUntil && auth.status === 'AUTH_APPROVED' && (
                                <div className="mt-2 text-xs text-base-content/50">
                                    Valida ate:{' '}
                                    {format(new Date(auth.validUntil), 'dd/MM/yyyy', {
                                        locale: ptBR,
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
